/**
 * CMS Client - Centralized Content Management API Layer
 * Phase CMS-1: Foundation
 *
 * Purpose:
 * Provides typed API access to CMS tables (cms_tiers, cms_pricing, cms_features,
 * cms_upsell_messages, cms_quips, cms_experiments) with feature flag fallback
 * to lib/polar/constants.ts for safe rollout.
 *
 * Usage:
 * ```typescript
 * import { cmsClient } from '@/lib/cms/client';
 *
 * // Get tier content
 * const tier = await cmsClient.getTierContent('drama_plus');
 *
 * // Get pricing with fallback
 * const pricing = await cmsClient.getTierPricing('drama_plus', 'individual');
 * ```
 *
 * Feature Flag:
 * Set CMS_CONTENT_ENABLED=true in .env.local to use CMS data.
 * Falls back to constants.ts when disabled or on fetch error.
 */

import { createClient, type SupabaseClient } from '../test-utils/supabase-fetch';
import type { Database } from '../../packages/api-client/src/types/database-generated';
import { PRICING, TIER_NAMES } from '../polar/constants';

// ============================================================================
// Constants
// ============================================================================

/**
 * Tier order for feature gating comparison.
 * Higher index = more premium tier.
 */
export const TIER_ORDER: readonly TierId[] = [
  'anonymous',
  'free',
  'family_member',
  'drama_plus',
  'dramax',
] as const;

// ============================================================================
// Types
// ============================================================================

export type TierId = 'anonymous' | 'free' | 'drama_plus' | 'dramax' | 'family_member';
export type PlanType = 'individual' | 'family';
export type BillingInterval = 'week' | 'month' | 'quarter' | 'semester' | 'year';
export type UpsellContext = 'quota_exhausted' | 'quota_warning' | 'feature_gate' | 'upgrade_cta' | 'retention';
export type QuipCategory = 'pitching' | 'network' | 'budget' | 'talent' | 'trends' | 'meta' |
  'user_focused' | 'dogfooding_family' | 'loading' | 'error' | 'success';

// CMS table row types (from generated database types)
export type CMSTier = Database['public']['Tables']['cms_tiers']['Row'];
export type CMSPricing = Database['public']['Tables']['cms_pricing']['Row'];
export type CMSFeature = Database['public']['Tables']['cms_features']['Row'];
export type CMSUpsellMessage = Database['public']['Tables']['cms_upsell_messages']['Row'];
export type CMSQuip = Database['public']['Tables']['cms_quips']['Row'];
export type CMSExperiment = Database['public']['Tables']['cms_experiments']['Row'];

// Simplified types for components (matching legacy constants structure)
export interface TierContent {
  tier_id: TierId;
  display_name: string;
  tagline: string | null;
  description: string | null;
  badge: string | null;
  layer_number: number | null;
}

export interface PricingProduct {
  product_key: string;
  billing_interval: BillingInterval;
  price_cents: number;
  display_price: string;
  billed_as: string | null;
  per_person: string | null;
  daily_quota: number | null;
  savings_badge: string | null;
  promo_badge: string | null;
  highlight: boolean;
}

export interface UpsellMessage {
  message_key: string;
  target_tier_id: TierId | null;
  headline: string;
  body: string;
  cta_text: string;
  cta_secondary_text: string | null;
  urgency: 'low' | 'medium' | 'high' | null;
}

export interface Quip {
  quip_key: string;
  content: string;
  author: 'narrator' | 'drama_expert' | null;
  tone: 'playful' | 'insightful' | 'dramatic' | 'supportive' | null;
}

export interface Experiment {
  experiment_key: string;
  name: string;
  variants: Array<{
    key: string;
    weight: number;
    description?: string;
  }>;
  status: 'draft' | 'running' | 'paused' | 'completed';
}

// ============================================================================
// Logging Utility
// ============================================================================

/**
 * CMS-specific logger that can be disabled in production.
 * Uses console.warn for development visibility but can be silenced
 * via CMS_LOG_LEVEL environment variable.
 */
const cmsLogger = {
  warn: (message: string, ...args: unknown[]): void => {
    // Silently skip logging in production unless explicitly enabled
    try {
      const logLevel = typeof process !== 'undefined' && process.env
        ? process.env.CMS_LOG_LEVEL || process.env.EXPO_PUBLIC_CMS_LOG_LEVEL
        : undefined;

      // Only log if level is 'debug' or 'warn', or in development
      if (logLevel === 'debug' || logLevel === 'warn' || process.env.NODE_ENV === 'development') {
        console.warn(message, ...args);
      }
    } catch {
      // Silently fail if environment access throws
    }
  },
};

// ============================================================================
// Feature Flag
// ============================================================================

/**
 * Check if CMS content is enabled via feature flag.
 * Falls back to constants.ts when disabled (safe rollout).
 *
 * @returns true if CMS is enabled, false otherwise (safe default)
 */
export function isCMSEnabled(): boolean {
  try {
    if (typeof process !== 'undefined' && process.env) {
      return process.env.CMS_CONTENT_ENABLED === 'true' ||
             process.env.EXPO_PUBLIC_CMS_CONTENT_ENABLED === 'true';
    }
    return false;
  } catch {
    // If accessing process.env throws (some bundlers/environments),
    // default to disabled for safe rollout
    return false;
  }
}

// ============================================================================
// Supabase Client (Lazy Singleton)
// ============================================================================

/**
 * Supabase client singleton storage.
 * Uses closure pattern to avoid global mutable state while maintaining
 * singleton behavior for connection pooling efficiency.
 */
const getSupabaseClient = (() => {
  let client: SupabaseClient<Database> | null = null;

  return (): SupabaseClient<Database> => {
    if (client) return client;

    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('[CMS Client] Missing Supabase environment variables');
    }

    client = createClient<Database>(supabaseUrl, supabaseAnonKey);
    return client;
  };
})();

// ============================================================================
// CMS Client API
// ============================================================================

export const cmsClient = {
  /**
   * Get marketing content for a specific tier.
   * Falls back to TIER_NAMES constant if CMS disabled or fetch fails.
   */
  async getTierContent(tierId: TierId): Promise<TierContent> {
    // Feature flag check
    if (!isCMSEnabled()) {
      return this.getTierContentFallback(tierId);
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc('get_tier_content', { p_tier_id: tierId });

      if (error || !data || data.length === 0) {
        cmsLogger.warn(`[CMS Client] getTierContent failed for ${tierId}, using fallback:`, error?.message);
        return this.getTierContentFallback(tierId);
      }

      const row = data[0];
      return {
        tier_id: row.tier_id as TierId,
        display_name: row.display_name,
        tagline: row.tagline,
        description: row.description,
        badge: row.badge,
        layer_number: row.layer_number,
      };
    } catch (err) {
      cmsLogger.warn(`[CMS Client] getTierContent error for ${tierId}, using fallback:`, err);
      return this.getTierContentFallback(tierId);
    }
  },

  /**
   * Fallback to legacy constants.ts TIER_NAMES
   */
  getTierContentFallback(tierId: TierId): TierContent {
    const displayName = (TIER_NAMES as Record<string, string>)[tierId] || tierId;
    return {
      tier_id: tierId,
      display_name: displayName,
      tagline: null,
      description: null,
      badge: null,
      layer_number: tierId === 'free' || tierId === 'anonymous' ? 1 :
                    tierId === 'drama_plus' || tierId === 'family_member' ? 2 : 3,
    };
  },

  /**
   * Get all visible tiers for pricing page display.
   */
  async getAllTiers(): Promise<TierContent[]> {
    if (!isCMSEnabled()) {
      return Object.keys(TIER_NAMES).map((tierId) =>
        this.getTierContentFallback(tierId as TierId)
      );
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('cms_tiers')
        .select('*')
        .eq('is_visible', true)
        .order('sort_order');

      if (error || !data) {
        cmsLogger.warn('[CMS Client] getAllTiers failed, using fallback:', error?.message);
        return Object.keys(TIER_NAMES).map((tierId) =>
          this.getTierContentFallback(tierId as TierId)
        );
      }

      return data.map((row: Record<string, unknown>) => ({
        tier_id: row.tier_id as TierId,
        display_name: row.display_name,
        tagline: row.tagline,
        description: row.description,
        badge: row.badge,
        layer_number: row.layer_number,
      }));
    } catch (err) {
      cmsLogger.warn('[CMS Client] getAllTiers error, using fallback:', err);
      return Object.keys(TIER_NAMES).map((tierId) =>
        this.getTierContentFallback(tierId as TierId)
      );
    }
  },

  /**
   * Get pricing options for a tier and plan type.
   * Falls back to PRICING constant if CMS disabled or fetch fails.
   */
  async getTierPricing(tierId: TierId, planType: PlanType = 'individual'): Promise<PricingProduct[]> {
    if (!isCMSEnabled()) {
      return this.getTierPricingFallback(tierId, planType);
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc('get_tier_pricing', {
        p_tier_id: tierId,
        p_plan_type: planType,
      });

      if (error || !data || data.length === 0) {
        cmsLogger.warn(`[CMS Client] getTierPricing failed for ${tierId}/${planType}, using fallback:`, error?.message);
        return this.getTierPricingFallback(tierId, planType);
      }

      return data.map((row: Record<string, unknown>) => ({
        product_key: row.product_key,
        billing_interval: row.billing_interval as BillingInterval,
        price_cents: row.price_cents,
        display_price: row.display_price,
        billed_as: row.billed_as,
        per_person: row.per_person,
        daily_quota: row.daily_quota,
        savings_badge: row.savings_badge,
        promo_badge: row.promo_badge,
        highlight: row.highlight ?? false,
      }));
    } catch (err) {
      cmsLogger.warn(`[CMS Client] getTierPricing error for ${tierId}/${planType}, using fallback:`, err);
      return this.getTierPricingFallback(tierId, planType);
    }
  },

  /**
   * Fallback to legacy constants.ts PRICING
   */
  getTierPricingFallback(tierId: TierId, planType: PlanType): PricingProduct[] {
    const tierPricing = PRICING[tierId as keyof typeof PRICING];
    if (!tierPricing) return [];

    const planPricing = tierPricing[planType as keyof typeof tierPricing];
    if (!planPricing || typeof planPricing !== 'object') return [];

    // Convert legacy format to new format
    return Object.entries(planPricing)
      .filter(([key]) => ['weekly', 'monthly', 'quarterly', 'semester', 'yearly'].includes(key))
      .map(([interval, data]: [string, unknown]) => {
        const pricing = data as {
          price: number;
          display: string;
          billedAs?: string;
          perPerson?: string;
          dailyQuota?: number;
          savingsBadge?: string;
          promoBadge?: string;
          highlight?: boolean;
        };
        return {
          product_key: `${tierId}_${planType === 'family' ? 'family_' : ''}${interval}`,
          billing_interval: interval === 'yearly' ? 'year' : interval as BillingInterval,
          price_cents: Math.round(pricing.price * 100),
          display_price: pricing.display,
          billed_as: pricing.billedAs || null,
          per_person: pricing.perPerson || null,
          daily_quota: pricing.dailyQuota || null,
          savings_badge: pricing.savingsBadge || null,
          promo_badge: pricing.promoBadge || null,
          highlight: pricing.highlight || false,
        };
      });
  },

  /**
   * Get upsell message for a specific context and current tier.
   */
  async getUpsellMessage(currentTier: TierId, context: UpsellContext): Promise<UpsellMessage | null> {
    if (!isCMSEnabled()) {
      return null; // No fallback for upsell messages (CMS-only feature)
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc('get_upsell_message', {
        p_current_tier: currentTier,
        p_context: context,
      });

      if (error || !data || data.length === 0) {
        return null;
      }

      const row = data[0];
      return {
        message_key: row.message_key,
        target_tier_id: row.target_tier_id as TierId | null,
        headline: row.headline,
        body: row.body,
        cta_text: row.cta_text,
        cta_secondary_text: row.cta_secondary_text,
        urgency: row.urgency as UpsellMessage['urgency'],
      };
    } catch (err) {
      cmsLogger.warn(`[CMS Client] getUpsellMessage error for ${currentTier}/${context}:`, err);
      return null;
    }
  },

  /**
   * Get a random quip by category (for loading states, entertainment).
   */
  async getRandomQuip(category: QuipCategory, tierId?: TierId): Promise<Quip | null> {
    if (!isCMSEnabled()) {
      return null; // No fallback for quips (CMS-only feature)
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc('get_random_quip', {
        p_category: category,
        p_tier_id: tierId ?? undefined,
      });

      if (error || !data || data.length === 0) {
        return null;
      }

      const row = data[0];
      return {
        quip_key: row.quip_key,
        content: row.content,
        author: row.author as Quip['author'],
        tone: row.tone as Quip['tone'],
      };
    } catch (err) {
      cmsLogger.warn(`[CMS Client] getRandomQuip error for ${category}:`, err);
      return null;
    }
  },

  /**
   * Get experiment configuration by key.
   */
  async getExperiment(experimentKey: string): Promise<Experiment | null> {
    if (!isCMSEnabled()) {
      return null; // No fallback for experiments (CMS-only feature)
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc('get_experiment', {
        p_experiment_key: experimentKey,
      });

      if (error || !data || data.length === 0) {
        return null;
      }

      const row = data[0];
      return {
        experiment_key: row.experiment_key,
        name: row.name,
        variants: row.variants as Experiment['variants'],
        status: row.status as Experiment['status'],
      };
    } catch (err) {
      cmsLogger.warn(`[CMS Client] getExperiment error for ${experimentKey}:`, err);
      return null;
    }
  },

  /**
   * Get all features with tier gating information.
   */
  async getAllFeatures(): Promise<CMSFeature[]> {
    if (!isCMSEnabled()) {
      return []; // No fallback for features (CMS-only)
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('cms_features')
        .select('*')
        .eq('is_visible', true)
        .order('sort_order');

      if (error || !data) {
        return [];
      }

      return data;
    } catch (err) {
      cmsLogger.warn('[CMS Client] getAllFeatures error:', err);
      return [];
    }
  },

  /**
   * Check if a feature is available for a given tier.
   */
  async isFeatureAvailable(featureKey: string, currentTier: TierId): Promise<boolean> {
    if (!isCMSEnabled()) {
      // Default to tier-based logic without CMS
      const currentIndex = TIER_ORDER.indexOf(currentTier);
      // Drama+ features available for drama_plus and above
      if (['pattern_detection', 'casting_influences', 'rewrite_suggestions'].includes(featureKey)) {
        return currentIndex >= TIER_ORDER.indexOf('drama_plus');
      }
      // DramaX features available only for dramax
      if (['transformation_insights', 'nc_framework', 'viral_hooks', 'api_access'].includes(featureKey)) {
        return currentTier === 'dramax';
      }
      return true; // Default to available
    }

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('cms_features')
        .select('min_tier_id')
        .eq('feature_key', featureKey)
        .single();

      if (error || !data) {
        return true; // Default to available on error
      }

      const minTierIndex = TIER_ORDER.indexOf(data.min_tier_id as TierId);
      const currentTierIndex = TIER_ORDER.indexOf(currentTier);

      return currentTierIndex >= minTierIndex;
    } catch (err) {
      cmsLogger.warn(`[CMS Client] isFeatureAvailable error for ${featureKey}:`, err);
      return true;
    }
  },
};

export default cmsClient;
